export const profileRestrictedTypes = ['STUDENT', 'PARENT'];

export interface IQualificationRecord {
  degreeTitle: string;
  degreeType: string;
  customDegreeType?: string;
  passingYear: number;
}

export type IProfessionalTrainingRecord =
  | {
      /**
       * Sentinel record used by FE to represent "no trainings".
       * This is intentionally a lightweight object so the payload can
       * explicitly convey the absence of trainings.
       */
      status: 'No_Trainings';
      trainingTitle?: never;
      givenBy?: never;
      date?: never;
    }
  | {
      trainingTitle: string;
      givenBy: string;
      date: string;
      status?: undefined;
    };

export interface UserToken {
  id: number;
  access: string;
  refresh: string;
  refreshExpiry: string;
}

/**
 * id is the user id.
 * recordId is the id of the record in the local db.
 */

// creating new interface for invited profiles will update once once whole flow is implemented
export interface IUserProfileInvite {
  username: string;
  uuid: string;
  name: string;
}
type invitationStatus =
  | ProfileStatus.Invited
  | ProfileStatus.Accepted
  | ProfileStatus.NotInvited
  | ProfileStatus.Pending
  | ProfileStatus.Failed;

export enum ProfileStatus {
  Invited = 'Invited',
  Accepted = 'Accepted',
  NotInvited = 'Not Invited',
  Pending = 'Pending',
  Failed = 'Failed',
}
export interface GenericProfileV1 {
  id: number;
  uuid: string;
  isActive?: boolean;
  joiningDate?: string | null;
  role: string;
  type: string;
  invitationStatus: invitationStatus;
  isSelected?: boolean; // is set to check which profile is currently locally active.
  roleId: number;
  sentByProfileModel: string;
  sentByProfileId: number;
  name: string;
  username: string;
  model?: string;
  sentBy?: number;
}

interface IProfileBaseInformation {
  yearsofService: number;
  userUsername: string;
  dateOfBirth?: string | null;
  lastPromotionDate?: string | null;
  qualifications?: IQualificationRecord[];
  professionalTrainings?: IProfessionalTrainingRecord[];
  gender?: string;
  jobType?: JobType;
  serviceDesignation?: ServiceDesignation;
  basicPayScale?: BasicPayScale;
}
export interface ITeacherProfileV1
  extends GenericProfileV1,
    IProfileBaseInformation {
  cnic: string;
  school: number;
  emis: number;
  schoolName: string;
  levels: string[];
  joiningDate?: string;
}

export interface IPrincipalProfileV1
  extends GenericProfileV1,
    IProfileBaseInformation {
  cnic: string;
  school: number;
  emis: number;
  schoolName: string;
  levels: string[];
}
export interface ICoachProfileV1
  extends GenericProfileV1,
    IProfileBaseInformation {
  cnic: string;
  schools: number[];
  emis: number[];
  levels: string[];
}

export interface IRegionalManager
  extends GenericProfileV1,
    IProfileBaseInformation {
  cnic: string;
  schoolGroup: number;
}

export interface ISchools {
  uuid?: string;
  id?: number;
  name: string;
  address: string;
  city: string;
  emis: number;
  group: number;
  region: null | number;
  numberOfStudents: number;
  numberOfTeachers: number;
  profileType?: string;
}

// School registration interface for push sync
export interface ISchoolRegistration {
  uuid: string;
  name: string;
  emis: number;
  address: string;
  city: string;
  region?: string | null;
  group: number;
  profileType?: string;
  profile?: number;
}
export interface ISchoolRegions {
  id: number;
  name: string;
}

export interface ISchoolGroups extends ISchoolRegions {
  schoolIds: number[];
  parent: number | null;
  descendentGroupIds: number[];
}
export interface IUserInformation {
  id?: number;
  uuid: string;
  username: string;
  name: string;
  additionalPhoneNumbers?: string[];
}

// user interface for v1

export interface BaseUserProfile {
  recordId: number;
  id: number;
  username: string;
  name: string;
  featureFlags: FeatureFlags;
  url: string;
}

export interface UserProfile extends BaseUserProfile {
  profiles: ITeacherProfile[];
}

export interface GenericProfile {
  id: number;
  isActive: boolean;
  // is set to check which profile is currently locally active.
  isSelected: boolean;
  role: number;
  type: string;
  label?: string;
  participantId: string;
}

export interface ITeacherProfile extends GenericProfile {
  cnic: string;
  joiningDate: string;
  school: number;
  schoolName: string;
  emis: number;
  yearsOfService: number;
  levels?: string[];
}
export interface IPrincipalProfile extends GenericProfile {
  cnic: string;
  joiningDate: null;
  school: number;
  emis: number;
  yearsOfService: number;
}
export interface ICoachProfile extends GenericProfile {
  cnic: string;
  joiningDate: null;
  school: number[];
  schoolGroup: number;
  yearsOfService: number;
}
export interface IAllCoachProfile {
  id: number;
  name: string;
  schools: number[];
}

export interface IAreaEducationOfficer
  extends GenericProfileV1,
    IProfileBaseInformation {
  cnic: string;
  joiningDate: null;
  schoolGroup: number;
}
export interface IAdministrator
  extends GenericProfileV1,
    IProfileBaseInformation {
  cnic: string;
  joiningDate: null;
  schoolGroup: number;
}
export interface IProgramManager
  extends GenericProfileV1,
    IProfileBaseInformation {
  cnic: string;
  joiningDate: null;
  schoolGroup: number;
}
export interface Schools {
  id: number;
  name: string;
  emis: number;
  schoolGroup: number;
  schoolGroupName: string;
  schoolType: string;
  address: string;
  city: string;
  district: string;
  province: string;
}

export interface TBConfig {
  appLogoPath: string;
  appLogoWidthNavbar: string;
  appLogoWidthLanding: string;
  app: string; // taleemabad | niete to check if this is the deployment for NIETE.
}

export interface FeatureFlags {
  [key: string]: any;
}

export enum UserRole {
  Teacher = 'TEACHER',
  Student = 'STUDENT',
  Parent = 'PARENT',
  Principal = 'PRINCIPAL',
  Coach = 'COACH',
}

export enum SchoolLevel {
  PRIMARY = 'PRIMARY',
  MIDDLE = 'MIDDLE',
  HIGH = 'HIGH',
}
enum UserActions {
  Read = 'read',
  Create = 'create',
  Update = 'update',
  Delete = 'delete',
}
export interface AccessControl {
  id: number;
  feature: number;
  actions: (keyof typeof UserActions)[];
  userRole: number;
  canManageProfiles: string[];
}
export interface IProfileLabels {
  id: number;
  profileType: string;
  label: string;
}
export interface IUserRolesAndPermissions {
  id: number;
  name: string;
  defaultRoute: string;
  accessControls: AccessControl[];
  parent: number | null;
}

export interface IFeatures {
  id: number;
  name: string;
}

export interface PermissionInterface {
  action: string;
  subject: string;
}

export interface IAreaEducationOfficerProfile extends GenericProfile {
  user: number;
  cnic: string;
  joiningDate: string | null;
  yearsOfService: number | null;
  schoolGroup: number;
  invitationStatus: string;
  sentBy: number | null;
}

export interface IRegionalManagerProfile extends GenericProfile {
  user: number;
  cnic: string;
  joiningDate: string | null;
  yearsOfService: number | null;
  schoolGroup: number;
  invitationStatus: string;
  sentBy: number | null;
}

export interface IProgramManagerProfile extends GenericProfile {
  user: number;
  cnic: string;
  joiningDate: string | null;
  yearsOfService: number | null;
  schoolGroup: number;
  invitationStatus: string;
  sentBy: number | null;
}

export interface IAdministratorProfile extends GenericProfile {
  user: number;
  cnic: string;
  joiningDate: string | null;
  yearsOfService: number | null;
  invitationStatus: string;
  sentBy: number | null;
}

export interface ISchoolGroup {
  id: number;
  name: string;
  parent: number | null;
  schoolIds: number[];
}

export interface ISchoolRegion {
  id: number;
  name: string;
}

// Transfer user profile interfaces
export interface ITransferUserProfile {
  uuid: string;
  prevProfileUuid: string;
  prevProfileModelName: string;
  currentProfileUuid: string;
  currentProfileModel: string;
  transferredByProfileId: string;
  transferredByProfileModel: string;
}

// Job Type enum
export enum JobType {
  PERMANENT = 'permanent',
  CONTRACT = 'contract',
}

export enum ServiceDesignation {
  SST = 'SST (Subject Specialist Teacher)',
  SET = 'SET (Secondary Education Teacher)',
  EST = 'EST (Elementary School Teacher)',
  OTHERS = 'OTHERS',
}

export enum BasicPayScale {
  NA = 'NA',
  BPS_8 = 'BPS-8',
  BPS_9 = 'BPS-9',
  BPS_10 = 'BPS-10',
  BPS_11 = 'BPS-11',
  BPS_12 = 'BPS-12',
  BPS_13 = 'BPS-13',
  BPS_14 = 'BPS-14',
  BPS_15 = 'BPS-15',
  BPS_16 = 'BPS-16',
  BPS_17 = 'BPS-17',
  BPS_18 = 'BPS-18',
  BPS_19 = 'BPS-19',
  BPS_20 = 'BPS-20',
  BPS_21 = 'BPS-21',
}
